import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  Button,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import { generateToolUrl } from '../../../config/toolsConfig'
import { generateInspectorUrl } from '../../../utils/urls'
import { userCanAccessInspectorItem, type FpUserAccessKind } from '../../../config/fpUserAccess'
import { useAuth } from '../../../contexts/AuthContext'
import { useInspector } from '../../../contexts/InspectorContext'
import { poolwerxColors } from '../../../theme/colors'

// Per-card accent colours used only to visually distinguish audit tools in
// the dashboard grid — not part of the Poolwerx brand palette. Keep them
// here rather than in theme/colors.ts so brand tokens stay clean.
const AUDIT_TOOL_ACCENTS = {
  productCatalogue: poolwerxColors.teal,
  cogsReview: '#8e44ad',
  healthCheck: '#4caf50',
  inventoryBalance: '#2196f3',
} as const

interface AuditToolDef {
  id: string
  toolId: string
  label: string
  description: string
  Icon: React.ComponentType<{ sx?: any }>
  accentColor: string
  // Which FP_USER access gate applies to this card. Health Check lives as a
  // top-level page route; the others are Inspector tools.
  fpUserAccess: { kind: FpUserAccessKind; id: string }
}

const AUDIT_TOOLS: AuditToolDef[] = [
  {
    id: 'product-catalogue-review',
    toolId: 'product_catalogue_review',
    label: 'Product Catalogue Review',
    description: 'Review products for usage, categorisation, and data completeness.',
    Icon: Inventory2OutlinedIcon,
    accentColor: AUDIT_TOOL_ACCENTS.productCatalogue,
    fpUserAccess: { kind: 'tool', id: 'product_catalogue_review' },
  },
  {
    id: 'cogs-review',
    toolId: 'cogs_review',
    label: 'COGS Review',
    description: 'Review COGS totals between Lightspeed and Xero.',
    Icon: PriceChangeOutlinedIcon,
    accentColor: AUDIT_TOOL_ACCENTS.cogsReview,
    fpUserAccess: { kind: 'tool', id: 'cogs_review' },
  },
  {
    id: 'health-check',
    toolId: 'health_check',
    label: 'Health Check',
    description: 'Run the full multi-area data health audit for your business.',
    Icon: MonitorHeartOutlinedIcon,
    accentColor: AUDIT_TOOL_ACCENTS.healthCheck,
    fpUserAccess: { kind: 'page', id: 'health_check' },
  },
  {
    id: 'inventory-balance',
    toolId: 'inventory_balance_review',
    label: 'Inventory Balance',
    description: 'Compare inventory balances across outlets and Xero.',
    Icon: BalanceOutlinedIcon,
    accentColor: AUDIT_TOOL_ACCENTS.inventoryBalance,
    fpUserAccess: { kind: 'tool', id: 'inventory_balance_review' },
  },
]

interface AuditToolCardProps {
  tool: AuditToolDef
  fpCode: string
  tenureId?: number
}

const AuditToolCard: React.FC<AuditToolCardProps> = ({ tool, fpCode, tenureId }) => {
  const to = generateToolUrl(fpCode, tool.toolId, tenureId)
  const { Icon } = tool

  return (
    <Paper
      variant="outlined"
      component={RouterLink}
      to={to}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        borderColor: 'grey.200',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'text.primary',
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: tool.accentColor,
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          '& .tool-arrow': {
            transform: 'translateX(3px)',
          },
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: `${tool.accentColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.25,
        }}
      >
        <Icon sx={{ fontSize: 22, color: tool.accentColor }} />
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {tool.label}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flex: 1, lineHeight: 1.45 }}
      >
        {tool.description}
        {' '}
        <ArrowForwardIcon
          className="tool-arrow"
          sx={{
            fontSize: 16,
            verticalAlign: 'middle',
            color: poolwerxColors.teal,
            transition: 'all 0.15s',
          }}
        />
      </Typography>
    </Paper>
  )
}

interface AuditToolsGridProps {
  fpCode: string
}

export const AuditToolsGrid: React.FC<AuditToolsGridProps> = ({ fpCode }) => {
  const { user } = useAuth()
  const { franchiseData } = useInspector()
  const tenureId = franchiseData?.id
  const visibleTools = AUDIT_TOOLS.filter((tool) =>
    userCanAccessInspectorItem(user?.role, tool.fpUserAccess.kind, tool.fpUserAccess.id),
  )

  if (visibleTools.length === 0) return null

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <HandymanOutlinedIcon sx={{ fontSize: 18, color: poolwerxColors.teal }} />
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.8, fontWeight: 600 }}
          >
            Audit tools
          </Typography>
        </Stack>
        <Button
          component={RouterLink}
          to={generateInspectorUrl(fpCode, { id: tenureId, subpath: 'tools' })}
          size="small"
          endIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          View all
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {visibleTools.map((tool) => (
          <Grid key={tool.id} item xs={12} sm={6} md={6} lg={3}>
            <AuditToolCard tool={tool} fpCode={fpCode} tenureId={tenureId} />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default AuditToolsGrid
